import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Save, Check, AlertCircle, Loader2, RotateCcw } from 'lucide-react';
import { useContent } from '../../context/ContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' },
];

const SECTIONS = [
  { key: 'hero', label: 'Hero Section' },
  { key: 'ctaBanner', label: 'CTA Banner (bottom)' },
  { key: 'contact', label: 'Contact Info' },
  { key: 'marketing', label: 'Marketing / Meta Pixel' },
];

const HERO_FIELDS = [
  { key: 'badge', label: 'Badge Text', type: 'input' },
  { key: 'tagline', label: 'Tagline (line 1)', type: 'input' },
  { key: 'tagline2', label: 'Tagline (line 2 - highlight)', type: 'input' },
  { key: 'subtitle', label: 'Subtitle', type: 'textarea' },
  { key: 'ctaFarmer', label: 'Farmer CTA Button', type: 'input' },
  { key: 'ctaDealer', label: 'Dealer CTA Button', type: 'input' },
];

const CTA_FIELDS = [
  { key: 'title', label: 'Banner Title', type: 'input' },
  { key: 'subtitle', label: 'Banner Subtitle', type: 'textarea' },
  { key: 'btnFarmer', label: 'Farmer Button', type: 'input' },
  { key: 'btnDealer', label: 'Dealer Button', type: 'input' },
];

const CONTACT_SHARED_FIELDS = [
  { key: 'phone', label: 'Primary Phone', placeholder: '+91 XXXXX XXXXX' },
  { key: 'phone2', label: 'Secondary Phone', placeholder: '+91 XXXXX XXXXX' },
  { key: 'whatsapp_number', label: 'WhatsApp Number (no +, e.g. 917696899983)', placeholder: '917696899983' },
  { key: 'email', label: 'Email', placeholder: 'name@example.com' },
  { key: 'facebook_url', label: 'Facebook URL', placeholder: 'https://facebook.com/...' },
];

const Input = ({ testId, value, onChange, placeholder, type = 'input' }) => {
  const Cmp = type === 'textarea' ? 'textarea' : 'input';
  return (
    <Cmp
      data-testid={testId}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={type === 'textarea' ? 3 : undefined}
      className="w-full px-3 py-2 border rounded-lg text-sm outline-none transition-all focus:ring-2 focus:ring-green-800/20"
      style={{ borderColor: '#E5E7EB' }}
    />
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: '#4A554A' }}>
      {label}
    </label>
    {children}
  </div>
);

export default function ContentEditor() {
  const { content, refresh } = useContent();
  const [draft, setDraft] = useState(null);
  const [activeSection, setActiveSection] = useState('hero');
  const [activeLang, setActiveLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: '', msg: '' });

  useEffect(() => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content]);

  const setHeroField = (field, value) => {
    setDraft((d) => ({ ...d, hero: { ...d.hero, [activeLang]: { ...d.hero[activeLang], [field]: value } } }));
  };
  const setCtaField = (field, value) => {
    setDraft((d) => ({ ...d, ctaBanner: { ...d.ctaBanner, [activeLang]: { ...d.ctaBanner[activeLang], [field]: value } } }));
  };
  const setContactShared = (field, value) => {
    setDraft((d) => ({ ...d, contact: { ...d.contact, [field]: value } }));
  };
  const setContactLocalized = (field, value) => {
    setDraft((d) => ({
      ...d,
      contact: { ...d.contact, [field]: { ...(d.contact[field] || {}), [activeLang]: value } },
    }));
  };
  const setMarketingField = (field, value) => {
    setDraft((d) => ({ ...d, marketing: { ...(d.marketing || {}), [field]: value } }));
  };

  const handleSave = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    setStatus({ type: '', msg: '' });
    try {
      const payload = { hero: draft.hero, ctaBanner: draft.ctaBanner, contact: draft.contact, marketing: draft.marketing };
      await axios.put(`${BACKEND_URL}/api/admin/content`, payload, { withCredentials: true });
      await refresh();
      setStatus({ type: 'success', msg: 'Saved & published live' });
      setTimeout(() => setStatus({ type: '', msg: '' }), 3500);
    } catch (err) {
      setStatus({ type: 'error', msg: err?.response?.data?.detail || 'Save failed. Try again.' });
    } finally {
      setSaving(false);
    }
  }, [draft, refresh]);

  const handleReset = () => {
    if (content) setDraft(JSON.parse(JSON.stringify(content)));
    setStatus({ type: '', msg: '' });
  };

  if (!draft) {
    return (
      <div className="flex items-center justify-center py-16 text-sm" style={{ color: '#6B7280' }}>
        <Loader2 className="animate-spin mr-2" size={16} /> Loading content...
      </div>
    );
  }

  const hero = draft.hero[activeLang] || {};
  const cta = draft.ctaBanner[activeLang] || {};
  const contact = draft.contact || {};
  const marketing = draft.marketing || {};

  return (
    <div data-testid="content-editor" className="max-w-4xl">
      {/* Info banner */}
      <div className="p-4 rounded-xl mb-5 text-xs flex items-start gap-2" style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}>
        <span>
          <strong>Shopify-style editing:</strong> changes save and publish live instantly. Switch language tabs to edit English, Hindi &amp; Punjabi.
        </span>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {SECTIONS.map((s) => (
          <button
            key={s.key}
            data-testid={`content-section-${s.key}`}
            onClick={() => setActiveSection(s.key)}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            style={{
              backgroundColor: activeSection === s.key ? '#0A3F22' : '#fff',
              color: activeSection === s.key ? '#fff' : '#4A554A',
              border: `1px solid ${activeSection === s.key ? '#0A3F22' : '#E5E7EB'}`,
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Language tabs */}
      <div className="flex gap-2 mb-5 border-b" style={{ borderColor: '#E5E7EB' }}>
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`content-lang-${l.code}`}
              onClick={() => setActiveLang(l.code)}
              className="px-4 py-2 text-sm font-semibold transition-all border-b-2 -mb-px"
              style={{
                borderColor: activeLang === l.code ? '#D97706' : 'transparent',
                color: activeLang === l.code ? '#0A3F22' : '#9CA3AF',
              }}
            >
              {l.label}
            </button>
          ))}
        </div>

      {/* Form body */}
      <div className="bg-white rounded-xl p-6 border space-y-4" style={{ borderColor: '#E5E7EB' }}>
        {activeSection === 'hero' && HERO_FIELDS.map((f) => (
          <Field key={f.key} label={f.label}>
            <Input testId={`hero-${activeLang}-${f.key}`} type={f.type} value={hero[f.key]} onChange={(v) => setHeroField(f.key, v)} />
          </Field>
        ))}

        {activeSection === 'ctaBanner' && CTA_FIELDS.map((f) => (
          <Field key={f.key} label={f.label}>
            <Input testId={`cta-${activeLang}-${f.key}`} type={f.type} value={cta[f.key]} onChange={(v) => setCtaField(f.key, v)} />
          </Field>
        ))}

        {activeSection === 'contact' && (
          <>
            <div className="text-xs font-semibold uppercase tracking-wider mb-2 pb-2 border-b" style={{ color: '#0A3F22', borderColor: '#E5E7EB' }}>
              Shared (all languages)
            </div>
            {CONTACT_SHARED_FIELDS.map((f) => (
              <Field key={f.key} label={f.label}>
                <Input testId={`contact-${f.key}`} value={contact[f.key]} placeholder={f.placeholder} onChange={(v) => setContactShared(f.key, v)} />
              </Field>
            ))}
            <div className="text-xs font-semibold uppercase tracking-wider mb-2 pb-2 border-b mt-4" style={{ color: '#0A3F22', borderColor: '#E5E7EB' }}>
              Localized ({LANGS.find((l) => l.code === activeLang).label})
            </div>
            <Field label="Address">
              <Input testId={`contact-address-${activeLang}`} type="textarea" value={contact.address?.[activeLang]} onChange={(v) => setContactLocalized('address', v)} />
            </Field>
            <Field label="Business Hours">
              <Input testId={`contact-hours-${activeLang}`} value={contact.hours?.[activeLang]} onChange={(v) => setContactLocalized('hours', v)} />
            </Field>
          </>
        )}

        {activeSection === 'marketing' && (
          <>
            <div className="p-3 rounded-lg text-xs mb-2" style={{ backgroundColor: '#EAF2EC', color: '#0A3F22' }}>
              <strong>Meta Pixel</strong> tracks visitors, form submits (Lead), Dealer applications (CompleteRegistration) and WhatsApp/Call clicks (Contact) — used by Facebook &amp; Instagram Ads for retargeting and conversion optimization.
              <br />
              Find your ID at <a href="https://business.facebook.com/events_manager" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Meta Events Manager</a>. Leave blank to disable tracking.
            </div>
            <Field label="Meta Pixel ID">
              <Input testId="marketing-meta-pixel-id" value={marketing.meta_pixel_id} placeholder="e.g. 1059303656878364" onChange={(v) => setMarketingField('meta_pixel_id', v)} />
            </Field>
          </>
        )}
      </div>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-white border-t mt-6 -mx-8 px-8 py-4 flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
        <div className="flex items-center gap-2 text-sm">
          {status.type === 'success' && (
            <span data-testid="content-save-success" className="flex items-center gap-1.5" style={{ color: '#059669' }}>
              <Check size={14} /> {status.msg}
            </span>
          )}
          {status.type === 'error' && (
            <span className="flex items-center gap-1.5" style={{ color: '#DC2626' }}>
              <AlertCircle size={14} /> {status.msg}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid="content-reset-btn"
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all hover:opacity-80"
            style={{ backgroundColor: '#F3F4F6', color: '#4A554A' }}
          >
            <RotateCcw size={14} /> Reset
          </button>
          <button
            data-testid="content-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: '#0A3F22' }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving...' : 'Save & Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}
